import { useState } from "react";
import { cn } from "@/lib/utils";
import type { ProductImage } from "@/types/models";

export interface ProductImageGalleryProps {
  images: ProductImage[];
  productName: string;
}

function ImagePlaceholder({ label }: { label: string }) {
  return (
    <div className="flex aspect-square w-full items-center justify-center bg-primary-100 dark:bg-primary-900 text-sm font-light text-primary-400">
      {label}
    </div>
  );
}

export default function ProductImageGallery({
  images,
  productName,
}: ProductImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (images.length === 0) {
    return <ImagePlaceholder label="Sem imagem" />;
  }

  const selectedImage = images[selectedIndex] ?? images[0];
  const mainUrl = selectedImage?.url;

  return (
    <div className="space-y-4">
      <div className="overflow-hidden border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-950">
        {mainUrl ? (
          <img
            src={mainUrl}
            alt={productName}
            className="aspect-square w-full object-cover"
          />
        ) : (
          <ImagePlaceholder label="Sem imagem" />
        )}
      </div>

      {images.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {images.map((image, index) => {
            const thumbUrl = image.thumbnailUrl ?? image.url;
            const isSelected = index === selectedIndex;

            return (
              <button
                key={image.id}
                type="button"
                onClick={() => setSelectedIndex(index)}
                aria-label={`Ver imagem ${index + 1}`}
                aria-pressed={isSelected}
                className={cn(
                  "h-16 w-16 shrink-0 overflow-hidden border transition-colors",
                  isSelected
                    ? "border-primary-900 dark:border-white"
                    : "border-primary-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-600"
                )}
              >
                {thumbUrl ? (
                  <img
                    src={thumbUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-primary-100 dark:bg-primary-900 text-xs font-light text-primary-400">
                    —
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
